import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { screenImageSource } from '../../lib/captureAssets.js';
import {
  annotationSupportsRotation,
  annotationSupportsTextEdit,
  createDrawAnnotation,
  createPenAnnotation,
  createAnnotationStyle,
  getOrderedAnnotations,
  materializeArrowPoints,
  normalizeAnnotation,
} from './feedbackAnnotations.js';
import {
  arrowheadGeometry,
  clampBounds,
  clampPanPosition,
  getBoundsCenter,
  getSelectionHandleWorldPosition,
  keyboardPanDelta,
  normalizeBounds,
  pointFromEvent,
  resizeBoundsFromHandle,
} from './feedbackGeometry.js';
import { AnnotationListPanel } from './AnnotationListPanel.jsx';
import { MarkupToolbar } from './MarkupToolbar.jsx';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import {
  FEEDBACK_TEXT_BACKGROUND_MODES,
  FEEDBACK_COLORS,
  FEEDBACK_CREATE_ACTIONS,
  colorValue,
  textBackgroundFill,
} from './feedbackTypes.js';
import { buildFeedbackTextLayout } from './feedbackTextLayout.js';
import { WorkspaceSplitter } from './WorkspaceSplitter.jsx';
import {
  PANEL_DEFAULT_PX,
  PANEL_MAX_PX,
  PANEL_MIN_PX,
  RESIZABLE_WORKSPACE_MIN_PX,
  RESIZE_MIN_VIEWPORT_PX,
  TRAY_DEFAULT_PX,
  TRAY_MAX_PX,
  TRAY_MIN_PX,
  ZOOM_MIN,
  anchoredScrollPosition,
  clampWorkspaceWidths,
  clampZoom,
  clearStoredWorkspaceLayout,
  readStoredWorkspaceLayout,
  steppedZoom,
  wheelZoom,
  writeStoredWorkspaceLayout,
} from './workspaceLayout.js';

const DRAW_TOOLS = new Set(['rectangle', 'arrow', 'highlight']);
const PLACE_TOOLS = new Set(['text', 'pin']);
const HANDLE_ORDER = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CONTEXT_MENU_WIDTH = 300;
const CONTEXT_MENU_HEIGHT = 520;

const FEEDBACK_CREATE_META = {
  text: { glyph: 'T', hint: 'Click to place' },
  rectangle: { glyph: '▭', hint: 'Drag to draw' },
  arrow: { glyph: '↗', hint: 'Drag to draw' },
  highlight: { glyph: '▰', hint: 'Drag to highlight' },
  pin: { glyph: '①', hint: 'Click to place' },
  pen: { glyph: '⌁', hint: 'Drag freehand' },
};

function formatSubmittedWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function pkgLifecycleLabel(pkg) {
  return pkg?.lifecycle === 'resolved' ? 'Resolved' : 'Submitted';
}

function historicalRoute(metadata) {
  if (!metadata?.route) return '';
  return `${metadata.route}${metadata.query || ''}${metadata.hash || ''}`;
}

function safeHistoricalRoute(metadata) {
  const route = String(metadata?.route || '');
  const containsControlCharacter = [...route].some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint <= 31 || codePoint === 127;
  });
  if (
    !route.startsWith('/') ||
    route.startsWith('//') ||
    route.includes('\\') ||
    containsControlCharacter
  ) {
    return '';
  }
  const query = String(metadata?.query || '');
  const hash = String(metadata?.hash || '');
  return `${route}${query.startsWith('?') ? query : ''}${hash.startsWith('#') ? hash : ''}`;
}

function historicalReporter(pkg) {
  return [pkg?.createdBy?.displayName, pkg?.createdBy?.persona].filter(Boolean).join(' · ');
}

function historicalViewport(viewport) {
  if (!viewport?.width || !viewport?.height) return '';
  return `${viewport.width}×${viewport.height}`;
}

function stagePercent(value, total) {
  return `${((Number(value) || 0) / (Number(total) || 1)) * 100}%`;
}

function clampMenuPosition(x, y) {
  if (typeof window === 'undefined') return { x, y };
  return {
    x: Math.max(12, Math.min(x, window.innerWidth - CONTEXT_MENU_WIDTH)),
    y: Math.max(12, Math.min(y, window.innerHeight - CONTEXT_MENU_HEIGHT)),
  };
}

function annotationTransform(annotation) {
  const { bounds, rotationDeg = 0 } = annotation;
  if (!annotationSupportsRotation(annotation.type) || !rotationDeg) {
    return `translate(${bounds.x} ${bounds.y})`;
  }
  return `translate(${bounds.x} ${bounds.y}) rotate(${rotationDeg} ${bounds.width / 2} ${bounds.height / 2})`;
}

function renderTextContent(annotation) {
  const { clipId, lines, metrics } = buildFeedbackTextLayout(annotation);
  const color = annotation.style?.textColor || '#0f172a';
  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect
            x="0"
            y="0"
            width={annotation.bounds.width}
            height={annotation.bounds.height}
            rx="8"
          />
        </clipPath>
      </defs>
      <text
        x={metrics.insetX}
        y={metrics.baselineY}
        fill={color}
        fontSize={metrics.fontSize}
        fontWeight="600"
        clipPath={`url(#${clipId})`}
      >
        {lines.map((line, index) => (
          <tspan
            key={`${annotation.id}-line-${index}`}
            x={metrics.insetX}
            dy={index === 0 ? 0 : metrics.lineHeight}
          >
            {line || ' '}
          </tspan>
        ))}
      </text>
    </>
  );
}

function AnnotationShape({
  annotation,
  selected,
  onMouseDown,
  onDoubleClick,
  onContextMenu,
  onKeyDown,
}) {
  const { bounds, style = {} } = annotation;
  const shared = {
    role: 'button',
    tabIndex: 0,
    className: selected ? 'selected' : '',
    'aria-label': `${annotation.id} ${annotation.type}`,
    'data-testid': `feedback-annotation-${annotation.id}`,
    onMouseDown,
    onDoubleClick,
    onContextMenu,
    onKeyDown,
  };

  if (annotation.type === 'arrow') {
    const [start, end] = materializeArrowPoints(annotation);
    const markerId = `feedback-arrowhead-${annotation.id}`;
    const head = arrowheadGeometry(start, end);
    return (
      <g {...shared} transform={annotationTransform(annotation)}>
        <defs>
          <marker
            id={markerId}
            markerUnits="userSpaceOnUse"
            markerWidth={head.headLength}
            markerHeight={head.headWidth}
            refX={head.headLength}
            refY={head.headWidth / 2}
            orient="auto"
          >
            <path
              d={`M0,0 L${head.headLength},${head.headWidth / 2} L0,${head.headWidth} z`}
              fill={style.strokeColor}
            />
          </marker>
        </defs>
        <line
          x1={start.x - bounds.x}
          y1={start.y - bounds.y}
          x2={end.x - bounds.x}
          y2={end.y - bounds.y}
          stroke={style.strokeColor}
          strokeWidth={head.strokeWidth}
          markerEnd={`url(#${markerId})`}
        />
      </g>
    );
  }

  if (annotation.type === 'pin') {
    const radius = Math.max(bounds.width, bounds.height) / 2;
    const cx = bounds.width / 2;
    const cy = bounds.height / 2;
    const label = String(annotation.id || '').replace(/^A-0*/, '') || '1';
    return (
      <g {...shared} transform={`translate(${bounds.x} ${bounds.y})`}>
        <circle cx={cx} cy={cy} r={radius} fill={style.strokeColor} />
        <text x={cx} y={cy + 7} fill="#fff" fontSize="20" fontWeight="700" textAnchor="middle">
          {label}
        </text>
      </g>
    );
  }

  if (annotation.type === 'highlight') {
    return (
      <g {...shared} transform={annotationTransform(annotation)}>
        <rect
          x="0"
          y="0"
          width={bounds.width}
          height={bounds.height}
          fill={style.fillColor || style.strokeColor}
          fillOpacity="0.26"
          stroke={style.strokeColor}
          strokeWidth="3"
          rx="4"
        />
      </g>
    );
  }

  if (annotation.type === 'text') {
    return (
      <g {...shared} transform={annotationTransform(annotation)}>
        <rect
          x="0"
          y="0"
          width={bounds.width}
          height={bounds.height}
          fill={textBackgroundFill(style.textBackgroundMode)}
          stroke={style.boxColor || style.strokeColor}
          strokeWidth="3"
          rx="8"
        />
        {renderTextContent(annotation)}
      </g>
    );
  }

  if (annotation.type === 'pen') {
    const points = (annotation.localPoints || []).map(
      (point) => `${point.x * bounds.width},${point.y * bounds.height}`,
    );
    return (
      <g {...shared} transform={`translate(${bounds.x} ${bounds.y})`}>
        <polyline
          points={points.join(' ')}
          fill="none"
          stroke={style.strokeColor}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    );
  }

  return (
    <g {...shared} transform={annotationTransform(annotation)}>
      <rect
        x="0"
        y="0"
        width={bounds.width}
        height={bounds.height}
        fill="transparent"
        stroke={style.strokeColor}
        strokeWidth="4"
        rx="4"
      />
    </g>
  );
}

function ContextMenu({
  menu,
  annotation,
  onCreate,
  onAction,
  onColorChange,
  onTextBackgroundChange,
  onNoteChange,
}) {
  if (!menu) return null;

  const style = {
    left: `${menu.x}px`,
    top: `${menu.y}px`,
  };

  if (menu.kind === 'canvas') {
    return (
      <div
        className="feedback-context-menu canvas"
        data-testid="feedback-empty-canvas-context-menu"
        role="dialog"
        aria-label="Create annotation"
        style={style}
      >
        <div className="feedback-context-menu-title">
          Add markup
          <span>Right-click an annotation to edit it.</span>
        </div>
        <div className="feedback-context-menu-grid">
          {FEEDBACK_CREATE_ACTIONS.map((action) => {
            const meta = FEEDBACK_CREATE_META[action.id] || {};
            return (
              <button
                key={action.id}
                type="button"
                className="feedback-context-menu-tool"
                data-testid={action.testId}
                aria-label={action.label}
                onClick={() => onCreate(action.id)}
              >
                <span className="feedback-context-menu-tool-glyph" aria-hidden>
                  {meta.glyph || ''}
                </span>
                <strong aria-hidden>{action.label}</strong>
                <span className="feedback-context-menu-tool-hint" aria-hidden>
                  {meta.hint || ''}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const isText = annotation?.type === 'text';
  const canRotate = annotationSupportsRotation(annotation?.type);
  const noteValue = isText ? annotation?.text || '' : annotation?.instruction || '';

  const transformButtons = [
    isText && { id: 'edit-text', glyph: '✎', label: 'Edit text', title: 'Edit text' },
    canRotate && { id: 'rotate-left', glyph: '⟲', label: 'Rotate left', title: 'Rotate -15°' },
    canRotate && { id: 'rotate-right', glyph: '⟳', label: 'Rotate right', title: 'Rotate +15°' },
    canRotate && {
      id: 'reset-rotation',
      glyph: '0°',
      label: 'Reset rotation',
      title: 'Reset rotation',
    },
    { id: 'duplicate', glyph: '⧉', label: 'Duplicate', title: 'Duplicate' },
    { id: 'bring-forward', glyph: '⤴', label: 'Bring forward', title: 'Bring forward' },
    { id: 'send-backward', glyph: '⤵', label: 'Send backward', title: 'Send backward' },
  ].filter(Boolean);

  return (
    <div
      className="feedback-context-menu object"
      data-testid="feedback-object-context-menu"
      role="dialog"
      aria-label="Annotation context menu"
      style={style}
    >
      <div className="feedback-context-menu-title">
        {(annotation?.type || 'annotation').replace(/^./, (c) => c.toUpperCase())} actions
        <span>Press Esc or click away to close.</span>
      </div>

      <div
        className="feedback-context-menu-toolbar"
        role="toolbar"
        aria-label="Annotation transforms"
      >
        {transformButtons.map((btn) => (
          <button
            key={btn.id}
            type="button"
            className="feedback-context-icon-btn"
            title={btn.title}
            aria-label={btn.label}
            onClick={() => onAction(btn.id)}
          >
            <span aria-hidden>{btn.glyph}</span>
          </button>
        ))}
        <button
          type="button"
          className="feedback-context-icon-btn danger"
          title="Delete annotation"
          aria-label="Delete"
          onClick={() => onAction('delete')}
        >
          <span aria-hidden>🗑</span>
        </button>
      </div>

      <label className="feedback-context-field">
        <span>{isText ? 'Text' : 'Note'}</span>
        <textarea
          data-testid={isText ? 'feedback-text-edit-mode' : 'feedback-annotation-note'}
          value={noteValue}
          rows={isText ? 4 : 3}
          onChange={(event) => onNoteChange(event.target.value)}
        />
      </label>

      {isText ? (
        <>
          <div className="feedback-context-field">
            <span>Text color</span>
            <div className="feedback-color-row">
              {FEEDBACK_COLORS.map((color) => (
                <button
                  key={`text-${color.id}`}
                  type="button"
                  className="feedback-color-chip"
                  style={{ '--swatch': color.value }}
                  aria-label={`Text color ${color.label}`}
                  onClick={() => onColorChange('textColor', color.value)}
                />
              ))}
            </div>
          </div>
          <div className="feedback-context-field">
            <span>Box color</span>
            <div className="feedback-color-row">
              {FEEDBACK_COLORS.map((color) => (
                <button
                  key={`box-${color.id}`}
                  type="button"
                  className="feedback-color-chip"
                  style={{ '--swatch': color.value }}
                  aria-label={`Box color ${color.label}`}
                  onClick={() => onColorChange('boxColor', color.value)}
                />
              ))}
            </div>
          </div>
          <div className="feedback-context-field">
            <span>Background</span>
            <div className="feedback-background-row" role="group" aria-label="Text box background">
              {FEEDBACK_TEXT_BACKGROUND_MODES.map((mode) => {
                const active = (annotation?.style?.textBackgroundMode || 'white') === mode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    className={`feedback-background-option ${active ? 'active' : ''}`}
                    aria-label={`Background ${mode.label}`}
                    aria-pressed={active}
                    onClick={() => onTextBackgroundChange(mode.id)}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="feedback-context-field">
          <span>{annotation?.type === 'highlight' ? 'Highlight color' : 'Stroke color'}</span>
          <div className="feedback-color-row">
            {FEEDBACK_COLORS.map((color) => (
              <button
                key={`stroke-${color.id}`}
                type="button"
                className="feedback-color-chip"
                style={{ '--swatch': color.value }}
                aria-label={`Annotation color ${color.label}`}
                onClick={() => onColorChange('strokeColor', color.value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectionOverlay({
  annotation,
  image,
  onHandleMouseDown,
  onHandleKeyDown,
  onRotateMouseDown,
  onRotateKeyDown,
}) {
  if (!annotation) return null;

  const { bounds, rotationDeg = 0, type } = annotation;
  const style = {
    left: stagePercent(bounds.x, image.width),
    top: stagePercent(bounds.y, image.height),
    width: stagePercent(bounds.width, image.width),
    height: stagePercent(bounds.height, image.height),
    transform: `rotate(${rotationDeg}deg)`,
  };

  return (
    <div
      className="feedback-selection-overlay"
      data-testid="feedback-selected-annotation-bounds"
      style={style}
    >
      {HANDLE_ORDER.map((handle) => (
        <button
          key={handle}
          type="button"
          className={`feedback-selection-handle ${handle}`}
          data-testid={`feedback-resize-handle-${handle}`}
          aria-label={`Resize ${handle}`}
          onMouseDown={(event) => onHandleMouseDown(event, handle)}
          onKeyDown={(event) => onHandleKeyDown(event, handle)}
        />
      ))}
      {annotationSupportsRotation(type) ? (
        <button
          type="button"
          className="feedback-selection-handle rotate"
          data-testid="feedback-rotate-handle"
          aria-label="Rotate annotation"
          onMouseDown={onRotateMouseDown}
          onKeyDown={onRotateKeyDown}
        />
      ) : null}
    </div>
  );
}

export function MarkupStage() {
  const {
    state,
    dispatch,
    armTool,
    clearTool,
    createPointAnnotationOnStage,
    createDrawAnnotationOnStage,
    createPenAnnotationOnStage,
    updateAnnotation,
    replaceAnnotations,
    deleteSelectedAnnotation,
    startTextEditing,
    stopTextEditing,
    completeStage,
    deleteStage,
    flushStageAutosave,
    saveDraft,
    openReview,
    exportPackage,
    openScreenForEditing,
    navigate,
  } = useFeedbackMarkup();
  const { user, roles = [] } = useCurrentUser();
  const stage = state.stage;
  const stageRef = useRef(null);
  const menuRef = useRef(null);
  const interactionRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [menu, setMenu] = useState(null);
  const [interactionType, setInteractionType] = useState(null);
  const [previewAnnotations, setPreviewAnnotations] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [draftAnnotation, setDraftAnnotation] = useState(null);

  // ---- workspace column sizing (drag/keyboard resizable rails, 508) ----
  const bodyRef = useRef(null);
  const [workspaceWidths, setWorkspaceWidths] = useState(
    () =>
      readStoredWorkspaceLayout(user?.id) || { tray: TRAY_DEFAULT_PX, panel: PANEL_DEFAULT_PX },
  );
  const [canResizeColumns, setCanResizeColumns] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= RESIZE_MIN_VIEWPORT_PX,
  );
  const [containerCompact, setContainerCompact] = useState(false);
  const columnsAreResizable = canResizeColumns && !containerCompact;
  const workspaceAvailableWidth = useCallback(
    () => bodyRef.current?.clientWidth || window.innerWidth,
    [],
  );
  const clampForCurrentWorkspace = useCallback(
    (next, available = workspaceAvailableWidth()) =>
      clampWorkspaceWidths({ ...next, available }),
    [workspaceAvailableWidth],
  );
  const reclampCurrentWorkspace = useCallback(
    (available = workspaceAvailableWidth()) => {
      setWorkspaceWidths((current) => {
        const clamped = clampWorkspaceWidths({ ...current, available });
        return clamped.tray === current.tray && clamped.panel === current.panel
          ? current
          : clamped;
      });
    },
    [workspaceAvailableWidth],
  );
  const applyWorkspaceWidths = useCallback(
    (next) => {
      const clamped = clampForCurrentWorkspace(next);
      setWorkspaceWidths(clamped);
      writeStoredWorkspaceLayout(user?.id, clamped);
    },
    [clampForCurrentWorkspace, user?.id],
  );
  useEffect(() => {
    const onWindowResize = () => {
      const resizable = window.innerWidth >= RESIZE_MIN_VIEWPORT_PX;
      setCanResizeColumns(resizable);
      if (resizable) reclampCurrentWorkspace();
    };
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, [reclampCurrentWorkspace]);
  // Stored layout is per-actor. Clamp before paint so a wide-display layout
  // can never flash/apply rails that collapse the stage on a narrower laptop.
  useLayoutEffect(() => {
    const restored =
      readStoredWorkspaceLayout(user?.id) || { tray: TRAY_DEFAULT_PX, panel: PANEL_DEFAULT_PX };
    setWorkspaceWidths(canResizeColumns ? clampForCurrentWorkspace(restored) : restored);
  }, [canResizeColumns, clampForCurrentWorkspace, user?.id]);
  // The workspace can narrow without a window resize (shell rail, browser
  // zoom, container layout). Reapply the same stage-minimum clamp whenever
  // the actual grid container changes size.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || !canResizeColumns) {
      setContainerCompact(false);
      return undefined;
    }
    const updateForAvailableWidth = (available) => {
      const compact = available < RESIZABLE_WORKSPACE_MIN_PX;
      setContainerCompact(compact);
      if (!compact) reclampCurrentWorkspace(available);
    };
    updateForAvailableWidth(body.clientWidth || window.innerWidth);
    const WorkspaceResizeObserver = window.ResizeObserver;
    if (!WorkspaceResizeObserver) return undefined;
    const observer = new WorkspaceResizeObserver((entries) => {
      const available = entries[0]?.contentRect?.width || body.clientWidth || window.innerWidth;
      updateForAvailableWidth(available);
    });
    observer.observe(body);
    return () => observer.disconnect();
  }, [canResizeColumns, reclampCurrentWorkspace]);
  const resetWorkspaceWidths = useCallback(() => {
    clearStoredWorkspaceLayout(user?.id);
    const defaults = { tray: TRAY_DEFAULT_PX, panel: PANEL_DEFAULT_PX };
    setWorkspaceWidths(columnsAreResizable ? clampForCurrentWorkspace(defaults) : defaults);
  }, [clampForCurrentWorkspace, columnsAreResizable, user?.id]);

  // ---- stage zoom (fit / fill / manual with cursor-anchored wheel) ----
  const stageViewportRef = useRef(null);
  const [stageZoom, setStageZoom] = useState({ mode: 'fit', level: 1 });
  const stageZoomRef = useRef(stageZoom);
  stageZoomRef.current = stageZoom;
  const zoomFrameRef = useRef(null);
  const [panModeEnabled, setPanModeEnabled] = useState(false);
  const [spacePanActive, setSpacePanActive] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const panModeRef = useRef(panModeEnabled);
  const spaceHeldRef = useRef(false);
  const pointerOverStageRef = useRef(false);
  const panGestureRef = useRef(null);
  panModeRef.current = panModeEnabled;
  const stageScreenKey = stage?.screenId || stage?.metadata?.screenId || stage?.id;

  const setViewportScroll = useCallback((nextPosition) => {
    const viewport = stageViewportRef.current;
    if (!viewport) return;
    // A pan supersedes any pending zoom-centering frame: without this cancel,
    // the frame lands after the pan and silently overwrites the new offsets.
    if (zoomFrameRef.current != null) {
      cancelAnimationFrame(zoomFrameRef.current);
      zoomFrameRef.current = null;
    }
    const next = clampPanPosition(nextPosition, viewport);
    viewport.scrollLeft = next.left;
    viewport.scrollTop = next.top;
  }, []);

  const resetStageView = useCallback(() => {
    if (zoomFrameRef.current != null) {
      cancelAnimationFrame(zoomFrameRef.current);
      zoomFrameRef.current = null;
    }
    const fit = { mode: 'fit', level: 1 };
    stageZoomRef.current = fit;
    setStageZoom(fit);
    setViewportScroll({ left: 0, top: 0 });
  }, [setViewportScroll]);

  useLayoutEffect(() => {
    resetStageView();
    setPanModeEnabled(false);
    setSpacePanActive(false);
    setIsPanning(false);
    spaceHeldRef.current = false;
    panGestureRef.current = null;
  }, [resetStageView, stageScreenKey]);

  const applyManualZoom = useCallback((nextLevel, anchor = null) => {
    const viewport = stageViewportRef.current;
    const stageEl = stageRef.current;
    const current = stageZoomRef.current;
    // Convert fit/fill to an equivalent manual level so steps feel continuous.
    const currentLevel =
      current.mode === 'manual'
        ? current.level
        : viewport && stageEl && viewport.clientWidth > 0
          ? stageEl.clientWidth / viewport.clientWidth
          : 1;
    const clamped = clampZoom(nextLevel(currentLevel));
    setStageZoom({ mode: 'manual', level: clamped });
    if (viewport) {
      const cursorX = anchor ? anchor.x : viewport.clientWidth / 2;
      const cursorY = anchor ? anchor.y : viewport.clientHeight / 2;
      const { scrollLeft, scrollTop } = anchoredScrollPosition({
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop,
        cursorX,
        cursorY,
        prevZoom: currentLevel,
        nextZoom: clamped,
      });
      if (zoomFrameRef.current != null) cancelAnimationFrame(zoomFrameRef.current);
      zoomFrameRef.current = requestAnimationFrame(() => {
        viewport.scrollLeft = scrollLeft;
        viewport.scrollTop = scrollTop;
        zoomFrameRef.current = null;
      });
    }
  }, []);

  // Plain mouse wheel over the stage zooms at the cursor (explicit tester ask).
  // Ctrl/Cmd+wheel is never intercepted (browser zoom); at minimum zoom a
  // zoom-out wheel is released so page scrolling is never trapped. React's
  // synthetic onWheel is passive, so the listener is attached manually.
  useEffect(() => {
    const viewport = stageViewportRef.current;
    if (!viewport) return undefined;
    const onWheel = (event) => {
      if (event.ctrlKey || event.metaKey) return;
      const current = stageZoomRef.current;
      // Fit/Max convert to an equivalent manual level, so wheel-out keeps
      // shrinking below Fit all the way to the true minimum; the event is
      // released (page scroll) only once that floor is reached.
      if (event.deltaY > 0 && current.mode === 'manual' && current.level <= ZOOM_MIN) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      applyManualZoom((level) => wheelZoom(level, event.deltaY), {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [applyManualZoom, stage?.screenId]);

  // Pan is a drag-to-scroll interaction on the existing overflow viewport.
  // Refs hold the high-frequency pointer state; React state only exposes the
  // user-visible cursor/toggle modes.
  useEffect(() => {
    const viewport = stageViewportRef.current;
    if (!viewport) return undefined;
    const isTypingTarget = (target) =>
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName) ||
      target?.isContentEditable ||
      Boolean(target?.closest?.('[contenteditable="true"]'));
    // A focused control OUTSIDE the stage (toolbar/zoom buttons, links) must
    // keep native Space activation; inside the viewport, Space-pan wins and
    // Enter remains the annotation-selection key.
    const isOutsideInteractiveTarget = (target) =>
      !viewport.contains(target) &&
      Boolean(
        target?.closest?.(
          'button, a[href], summary, [role="button"], [role="menuitem"], [role="tab"], [role="switch"], [role="checkbox"]',
        ),
      );
    const stageHasTypingFocus = () => {
      const activeElement = document.activeElement;
      return viewport.contains(activeElement) && isTypingTarget(activeElement);
    };
    const stageHasFocusOrHover = () =>
      pointerOverStageRef.current ||
      document.activeElement === viewport ||
      viewport.contains(document.activeElement);
    const endPan = () => {
      const current = panGestureRef.current;
      panGestureRef.current = null;
      if (current?.pointerId != null && viewport.hasPointerCapture?.(current.pointerId)) {
        viewport.releasePointerCapture(current.pointerId);
      }
      setIsPanning(false);
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };
    const onKeyDown = (event) => {
      if (
        panModeRef.current &&
        event.target === viewport &&
        event.key?.startsWith('Arrow')
      ) {
        const delta = keyboardPanDelta(event.key, event);
        if (!delta) return;
        event.preventDefault();
        event.stopPropagation();
        setViewportScroll({
          left: viewport.scrollLeft + delta.x,
          top: viewport.scrollTop + delta.y,
        });
        return;
      }
      if (
        event.code !== 'Space' ||
        isTypingTarget(event.target) ||
        isOutsideInteractiveTarget(event.target) ||
        stageHasTypingFocus() ||
        !stageHasFocusOrHover()
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (!spaceHeldRef.current) {
        spaceHeldRef.current = true;
        setSpacePanActive(true);
      }
    };
    const onKeyUp = (event) => {
      if (event.code !== 'Space' || !spaceHeldRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      spaceHeldRef.current = false;
      setSpacePanActive(false);
      if (panGestureRef.current?.source === 'space' && !panModeRef.current) endPan();
    };
    const onPointerDown = (event) => {
      const middleButtonPan = event.button === 1;
      const primaryButtonPan =
        event.button === 0 && (panModeRef.current || spaceHeldRef.current);
      if (!middleButtonPan && !primaryButtonPan) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      suppressClickRef.current = true;
      panGestureRef.current = {
        source: middleButtonPan ? 'middle' : panModeRef.current ? 'toggle' : 'space',
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: viewport.scrollLeft,
        scrollTop: viewport.scrollTop,
      };
      viewport.setPointerCapture?.(event.pointerId);
      setIsPanning(true);
    };
    const onPointerMove = (event) => {
      const pan = panGestureRef.current;
      if (!pan) return;
      if (pan.pointerId != null && event.pointerId !== pan.pointerId) return;
      if (event.buttons === 0) {
        endPan();
        return;
      }
      event.preventDefault();
      setViewportScroll({
        left: pan.scrollLeft - (event.clientX - pan.startX),
        top: pan.scrollTop - (event.clientY - pan.startY),
      });
    };
    const onPointerEnd = (event) => {
      const pan = panGestureRef.current;
      if (!pan || (pan.pointerId != null && event.pointerId !== pan.pointerId)) return;
      endPan();
    };
    const onPointerEnter = () => {
      pointerOverStageRef.current = true;
    };
    const onPointerLeave = () => {
      pointerOverStageRef.current = false;
    };
    const onWindowBlur = () => {
      spaceHeldRef.current = false;
      setSpacePanActive(false);
      endPan();
    };
    viewport.addEventListener('pointerdown', onPointerDown, true);
    viewport.addEventListener('pointerenter', onPointerEnter);
    viewport.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerEnd);
    document.addEventListener('pointercancel', onPointerEnd);
    viewport.addEventListener('lostpointercapture', endPan);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown, true);
      viewport.removeEventListener('pointerenter', onPointerEnter);
      viewport.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerEnd);
      document.removeEventListener('pointercancel', onPointerEnd);
      viewport.removeEventListener('lostpointercapture', endPan);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      pointerOverStageRef.current = false;
      endPan();
      document.body.classList.remove('fbws-resizing');
    };
  }, [setViewportScroll, stageScreenKey]);

  const image = useMemo(
    () => stage?.metadata?.image ?? { width: 1, height: 1 },
    [stage?.metadata?.image],
  );
  const orderedAnnotations = useMemo(
    () => getOrderedAnnotations(stage?.metadata?.annotations || []),
    [stage?.metadata?.annotations],
  );
  const annotations = previewAnnotations || orderedAnnotations;
  const renderAnnotations = draftAnnotation ? [...annotations, draftAnnotation] : annotations;
  const selectedAnnotation =
    annotations.find((annotation) => annotation.id === state.selectedAnnotationId) || null;
  const selectedTextLayout =
    selectedAnnotation?.type === 'text' ? buildFeedbackTextLayout(selectedAnnotation) : null;
  const readOnly = state.workspaceMode === 'view';
  const isSystemAdmin = Array.isArray(roles) && roles.includes(rbac.ROLE_IDS.SYSTEM_ADMIN);

  useEffect(() => {
    if (!menu) return undefined;
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenu(null);
    };
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const element = menuRef.current?.querySelector('.feedback-context-menu');
    if (!element) return;
    const rect = element.getBoundingClientRect();
    if (!rect.width && !rect.height) return;
    let nextX = menu.x;
    let nextY = menu.y;

    if (rect.right > window.innerWidth - 12) {
      nextX -= rect.right - (window.innerWidth - 12);
    }
    if (rect.bottom > window.innerHeight - 12) {
      nextY -= rect.bottom - (window.innerHeight - 12);
    }
    if (rect.left < 12) {
      nextX += 12 - rect.left;
    }
    if (rect.top < 12) {
      nextY += 12 - rect.top;
    }

    if (nextX !== menu.x || nextY !== menu.y) {
      setMenu((current) => (current ? { ...current, x: nextX, y: nextY } : current));
    }
  }, [menu]);

  useEffect(() => {
    setPreviewAnnotations(null);
    setDraftAnnotation(null);
    interactionRef.current = null;
    setInteractionType(null);
  }, [stage?.id]);

  const setPreviewFor = useCallback((annotationId, updater) => {
    const next = (interactionRef.current?.startAnnotations || []).map((annotation, index) =>
      annotation.id === annotationId ? updater(annotation, index) : annotation,
    );
    setPreviewAnnotations(next);
    interactionRef.current.previewAnnotations = next;
  }, []);

  const handleInteractionMove = useCallback(
    (event) => {
      if (!stageRef.current || !interactionRef.current) return;
      const point = pointFromEvent(event, stageRef.current, image);
      const current = interactionRef.current;

      if (current.type === 'move') {
        const dx = point.x - current.startPoint.x;
        const dy = point.y - current.startPoint.y;
        current.changed = current.changed || Math.abs(dx) > 0 || Math.abs(dy) > 0;
        setPreviewFor(current.annotationId, (annotation, index) =>
          normalizeAnnotation(
            {
              ...annotation,
              bounds: clampBounds(
                {
                  ...current.startAnnotation.bounds,
                  x: current.startAnnotation.bounds.x + dx,
                  y: current.startAnnotation.bounds.y + dy,
                },
                image,
              ),
            },
            index,
            image,
          ),
        );
      }

      if (current.type === 'resize') {
        current.changed = true;
        const nextBounds = clampBounds(
          resizeBoundsFromHandle(
            current.startAnnotation.bounds,
            current.startAnnotation.rotationDeg,
            current.handle,
            point,
          ),
          image,
        );
        setPreviewFor(current.annotationId, (annotation, index) =>
          normalizeAnnotation(
            {
              ...annotation,
              bounds: nextBounds,
              normalizedBounds: normalizeBounds(nextBounds, image),
            },
            index,
            image,
          ),
        );
      }

      if (current.type === 'rotate') {
        current.changed = true;
        const angle =
          (Math.atan2(point.y - current.center.y, point.x - current.center.x) * 180) / Math.PI;
        const rotationDeg = current.startRotationDeg + (angle - current.startAngleDeg);
        setPreviewFor(current.annotationId, (annotation, index) =>
          normalizeAnnotation(
            {
              ...annotation,
              rotationDeg,
            },
            index,
            image,
          ),
        );
      }

      if (current.type === 'draw') {
        current.changed = true;
        current.lastPoint = point;
        setDraftAnnotation(
          createDrawAnnotation({
            id: current.id,
            tool: current.tool,
            start: current.startPoint,
            end: point,
            image,
            strokeColor: current.strokeColor,
          }),
        );
      }

      if (current.type === 'pen') {
        const last = current.points[current.points.length - 1];
        if (!last || last.x !== point.x || last.y !== point.y) current.points.push(point);
        current.changed = current.points.length > 1;
        setDraftAnnotation(
          createPenAnnotation({
            id: current.id,
            points: current.points,
            image,
            strokeColor: current.strokeColor,
          }),
        );
      }
    },
    [image, setPreviewFor],
  );

  const handleInteractionEnd = useCallback(() => {
    const current = interactionRef.current;
    if (!current) return;
    // Swallow only the click that belongs to THIS mouseup (it fires
    // synchronously right after). If the drag released outside the stage no
    // click arrives at all — a stale flag would silently eat the tester's
    // next placement click (pin/text), so it clears itself on the next tick.
    suppressClickRef.current = true;
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
    interactionRef.current = null;
    setInteractionType(null);

    if (current.type === 'move' || current.type === 'resize' || current.type === 'rotate') {
      if (current.changed && current.previewAnnotations) {
        replaceAnnotations(current.previewAnnotations, current.annotationId);
      }
      setPreviewAnnotations(null);
      return;
    }

    if (current.type === 'draw') {
      if (current.changed) {
        createDrawAnnotationOnStage(
          current.tool,
          current.startPoint,
          current.lastPoint || current.startPoint,
        );
      }
      setDraftAnnotation(null);
      clearTool();
      return;
    }

    if (current.type === 'pen') {
      if (current.points.length > 1) {
        createPenAnnotationOnStage(current.points);
      }
      setDraftAnnotation(null);
      clearTool();
    }
  }, [clearTool, createDrawAnnotationOnStage, createPenAnnotationOnStage, replaceAnnotations]);

  useEffect(() => {
    if (!interactionType || interactionType.startsWith('keyboard-')) return undefined;
    window.addEventListener('mousemove', handleInteractionMove);
    window.addEventListener('mouseup', handleInteractionEnd);
    return () => {
      window.removeEventListener('mousemove', handleInteractionMove);
      window.removeEventListener('mouseup', handleInteractionEnd);
    };
  }, [
    clearTool,
    createDrawAnnotationOnStage,
    createPenAnnotationOnStage,
    handleInteractionEnd,
    handleInteractionMove,
    image,
    replaceAnnotations,
    interactionType,
  ]);

  const openCanvasMenu = useCallback(
    (event) => {
      if (readOnly || panModeEnabled || spaceHeldRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      dispatch({ type: 'annotation/clear-selection' });
      const position = clampMenuPosition(event.clientX, event.clientY);
      setMenu({
        kind: 'canvas',
        x: position.x,
        y: position.y,
      });
    },
    [dispatch, panModeEnabled, readOnly],
  );

  const openObjectMenu = useCallback(
    (annotation, event = null) => {
      const stageRect = stageRef.current?.getBoundingClientRect();
      const center = getBoundsCenter(annotation.bounds);
      const fallbackX = stageRect
        ? stageRect.left + (center.x / image.width) * stageRect.width
        : 120;
      const fallbackY = stageRect
        ? stageRect.top + (center.y / image.height) * stageRect.height
        : 120;
      const position = clampMenuPosition(event?.clientX || fallbackX, event?.clientY || fallbackY);
      setMenu({
        kind: 'object',
        annotationId: annotation.id,
        x: position.x,
        y: position.y,
      });
    },
    [image],
  );

  useEffect(() => {
    if (!stage) return undefined;
    const onKeyDown = (event) => {
      const editingFormField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName);
      const annotationKeyboardTarget = event.target?.closest?.(
        '[data-testid^="feedback-annotation-A-"]',
      );
      const interactiveControl = event.target?.closest?.(
        'button, a[href], input, textarea, select, [role="button"], [contenteditable="true"]',
      );
      if (event.key === 'Escape') {
        if (state.textEditingAnnotationId) stopTextEditing();
        else if (menu) setMenu(null);
        else if (interactionType) {
          interactionRef.current = null;
          setInteractionType(null);
          setPreviewAnnotations(null);
          setDraftAnnotation(null);
          clearTool();
        }
      }
      if (editingFormField) return;
      // Annotation shortcuts are global only so the SVG mark itself can use
      // them. Never cancel Enter on toolbar, dialog, link, or form controls.
      if (interactiveControl && !annotationKeyboardTarget) return;
      if (readOnly) return;
      if (
        (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) &&
        selectedAnnotation
      ) {
        event.preventDefault();
        openObjectMenu(selectedAnnotation);
      }
      if (event.key === 'Enter' && selectedAnnotation) {
        event.preventDefault();
        if (annotationSupportsTextEdit(selectedAnnotation.type)) {
          startTextEditing(selectedAnnotation.id);
        } else {
          // Non-text marks: Enter opens the note editor (W2 keyboard path).
          const noteSelectors = [
            selectedAnnotation.id
              ? `[data-testid="feedback-annotation-note-${selectedAnnotation.id}"]`
              : null,
            '[data-testid="feedback-primary-note"]',
            '[data-testid="feedback-annotation-note"]',
          ].filter(Boolean);
          for (const selector of noteSelectors) {
            const noteField = document.querySelector(selector);
            if (noteField) {
              noteField.focus();
              break;
            }
          }
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    clearTool,
    interactionType,
    menu,
    openObjectMenu,
    selectedAnnotation,
    stage,
    startTextEditing,
    state.textEditingAnnotationId,
    stopTextEditing,
    readOnly,
  ]);

  if (!stage) return null;

  function commitAnnotation(nextAnnotation) {
    updateAnnotation(nextAnnotation.id, nextAnnotation);
  }

  function updateSelectedText(value, targetAnnotation = selectedAnnotation) {
    if (readOnly) return;
    if (!targetAnnotation) return;
    // The canvas label seeds the note only while the note still mirrors it —
    // once the tester writes their own request text, editing the label must
    // never silently overwrite it (W2 single-source rule).
    const noteMirrorsLabel =
      !targetAnnotation.instruction || targetAnnotation.instruction === targetAnnotation.text;
    commitAnnotation(
      normalizeAnnotation(
        {
          ...targetAnnotation,
          text: value,
          ...(noteMirrorsLabel ? { instruction: value } : {}),
        },
        targetAnnotation.zIndex,
        image,
      ),
    );
  }

  function updateSelectedNote(value, targetAnnotation = selectedAnnotation) {
    if (readOnly) return;
    if (!targetAnnotation) return;
    commitAnnotation(
      normalizeAnnotation(
        {
          ...targetAnnotation,
          instruction: value,
        },
        targetAnnotation.zIndex,
        image,
      ),
    );
  }

  function updateSelectedColor(kind, value, targetAnnotation = selectedAnnotation) {
    if (readOnly) return;
    if (!targetAnnotation) return;
    let style = targetAnnotation.style || createAnnotationStyle(targetAnnotation.type);
    if (targetAnnotation.type === 'text') {
      style =
        kind === 'textColor'
          ? { ...style, textColor: value }
          : { ...style, boxColor: value, strokeColor: value };
    } else if (targetAnnotation.type === 'highlight') {
      style = { ...style, strokeColor: value, fillColor: value };
    } else {
      style = { ...style, strokeColor: value };
    }
    commitAnnotation(
      normalizeAnnotation(
        {
          ...targetAnnotation,
          style,
        },
        targetAnnotation.zIndex,
        image,
      ),
    );
  }

  function updateAnnotationColor(annotationId, colorId) {
    if (readOnly) return;
    const annotation = (stage.metadata?.annotations || []).find((item) => item.id === annotationId);
    if (!annotation) return;
    const color = colorValue(colorId);
    let style = annotation.style || createAnnotationStyle(annotation.type);
    if (annotation.type === 'text') {
      // One-click recolor changes what the tester sees as "the mark's color":
      // the text and its border together (fine-grained control stays in the
      // right-click menu, which recolors text and border independently).
      style = { ...style, boxColor: color, strokeColor: color, textColor: color };
    } else if (annotation.type === 'highlight') {
      style = { ...style, strokeColor: color, fillColor: color };
    } else {
      style = { ...style, strokeColor: color };
    }
    updateAnnotation(
      annotationId,
      normalizeAnnotation(
        {
          ...annotation,
          style,
        },
        annotation.zIndex,
        image,
      ),
    );
  }

  function updateSelectedTextBackground(mode, targetAnnotation = selectedAnnotation) {
    if (readOnly) return;
    if (!targetAnnotation || targetAnnotation.type !== 'text') return;
    dispatch({ type: 'text-background/set', mode });
    const style = {
      ...(targetAnnotation.style || createAnnotationStyle('text')),
      textBackgroundMode: mode,
    };
    commitAnnotation(
      normalizeAnnotation(
        {
          ...targetAnnotation,
          style,
        },
        targetAnnotation.zIndex,
        image,
      ),
    );
  }

  function handleObjectAction(action, targetAnnotation = selectedAnnotation) {
    if (readOnly) return;
    if (!targetAnnotation) return;
    if (action === 'edit-text') {
      setMenu(null);
      startTextEditing(targetAnnotation.id);
      return;
    }
    if (action === 'rotate-left' || action === 'rotate-right' || action === 'reset-rotation') {
      const rotationDeg =
        action === 'reset-rotation'
          ? 0
          : (targetAnnotation.rotationDeg || 0) + (action === 'rotate-left' ? -15 : 15);
      commitAnnotation(
        normalizeAnnotation(
          {
            ...targetAnnotation,
            rotationDeg,
          },
          targetAnnotation.zIndex,
          image,
        ),
      );
      setMenu(null);
      return;
    }
    if (action === 'duplicate') {
      dispatch({ type: 'annotation/duplicate', id: targetAnnotation.id });
      setMenu(null);
      return;
    }
    if (action === 'bring-forward') {
      dispatch({ type: 'annotation/bring-forward', id: targetAnnotation.id });
      setMenu(null);
      return;
    }
    if (action === 'send-backward') {
      dispatch({ type: 'annotation/send-backward', id: targetAnnotation.id });
      setMenu(null);
      return;
    }
    if (action === 'delete') {
      dispatch({ type: 'annotation/delete', id: targetAnnotation.id });
      setMenu(null);
    }
  }

  function beginTransform(event, type, annotation = selectedAnnotation, extras = {}) {
    if (readOnly) return;
    if (
      !annotation ||
      state.textEditingAnnotationId ||
      panModeEnabled ||
      spaceHeldRef.current
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setMenu(null);
    const point = pointFromEvent(event, stageRef.current, image);
    const startAnnotations = annotations.map((annotation) => ({ ...annotation }));
    const base = {
      type,
      annotationId: annotation.id,
      startAnnotation: { ...annotation },
      startAnnotations,
      changed: false,
      previewAnnotations: null,
    };
    if (type === 'move') {
      interactionRef.current = {
        ...base,
        startPoint: point,
      };
    }
    if (type === 'resize') {
      interactionRef.current = {
        ...base,
        handle: extras.handle,
      };
    }
    if (type === 'rotate') {
      const center = getBoundsCenter(annotation.bounds);
      interactionRef.current = {
        ...base,
        center,
        startRotationDeg: annotation.rotationDeg || 0,
        startAngleDeg: (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI,
      };
    }
    setInteractionType(type);
  }

  function finishKeyboardTransform({ commit = false } = {}) {
    const current = interactionRef.current;
    if (commit && current?.keyboard && current.changed && current.previewAnnotations) {
      replaceAnnotations(current.previewAnnotations, current.annotationId);
    }
    interactionRef.current = null;
    setInteractionType(null);
    setPreviewAnnotations(null);
  }

  function onTransformKeyDown(event, type, annotation, extras = {}) {
    const activationKey = event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
    const current = interactionRef.current;
    const currentMatches =
      current?.keyboard && current.type === type && current.annotationId === annotation?.id;

    if (!currentMatches) {
      if (!activationKey || !annotation) return;
      event.preventDefault();
      event.stopPropagation();
      const startAnnotations = annotations.map((item) => ({ ...item }));
      interactionRef.current = {
        keyboard: true,
        type,
        annotationId: annotation.id,
        handle: extras.handle,
        startAnnotations,
        previewAnnotations: startAnnotations,
        changed: false,
      };
      setPreviewAnnotations(startAnnotations);
      setInteractionType(`keyboard-${type}`);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finishKeyboardTransform();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      finishKeyboardTransform({ commit: true });
      return;
    }
    if (activationKey) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;

    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 12 : 4;
    const currentAnnotations = current.previewAnnotations || current.startAnnotations;
    const targetIndex = currentAnnotations.findIndex((item) => item.id === annotation.id);
    if (targetIndex < 0) return;
    const target = currentAnnotations[targetIndex];
    let nextAnnotation = target;

    if (type === 'resize') {
      const handlePoint = getSelectionHandleWorldPosition(
        target.bounds,
        target.rotationDeg || 0,
        extras.handle,
      );
      const pointer = {
        x:
          handlePoint.x +
          (event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0),
        y:
          handlePoint.y +
          (event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0),
      };
      const bounds = clampBounds(
        resizeBoundsFromHandle(target.bounds, target.rotationDeg || 0, extras.handle, pointer),
        image,
      );
      nextAnnotation = normalizeAnnotation(
        { ...target, bounds, normalizedBounds: normalizeBounds(bounds, image) },
        targetIndex,
        image,
      );
    } else if (type === 'rotate') {
      const delta = ['ArrowRight', 'ArrowUp'].includes(event.key) ? 2 : -2;
      nextAnnotation = normalizeAnnotation(
        { ...target, rotationDeg: (Number(target.rotationDeg) || 0) + delta },
        targetIndex,
        image,
      );
    }

    const nextAnnotations = currentAnnotations.map((item, index) =>
      index === targetIndex ? nextAnnotation : item,
    );
    current.previewAnnotations = nextAnnotations;
    current.changed = true;
    setPreviewAnnotations(nextAnnotations);
  }

  function onStageMouseDown(event) {
    if (readOnly) return;
    if (panModeEnabled || spaceHeldRef.current) return;
    if (event.button !== 0 || !stageRef.current) return;
    const point = pointFromEvent(event, stageRef.current, image);
    if (DRAW_TOOLS.has(state.activeTool)) {
      interactionRef.current = {
        type: 'draw',
        tool: state.activeTool,
        id: `draft-${state.activeTool}`,
        startPoint: point,
        lastPoint: point,
        strokeColor: colorValue(state.activeColor),
        changed: false,
      };
      setInteractionType('draw');
      setDraftAnnotation(
        createDrawAnnotation({
          id: `draft-${state.activeTool}`,
          tool: state.activeTool,
          start: point,
          end: point,
          image,
          strokeColor: colorValue(state.activeColor),
        }),
      );
      return;
    }

    if (state.activeTool === 'pen') {
      interactionRef.current = {
        type: 'pen',
        id: 'draft-pen',
        points: [point],
        strokeColor: colorValue(state.activeColor),
      };
      setInteractionType('pen');
      return;
    }
  }

  function onStageClick(event) {
    if (readOnly) return;
    if (panModeEnabled || spaceHeldRef.current) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (!stageRef.current) return;
    const point = pointFromEvent(event, stageRef.current, image);
    if (PLACE_TOOLS.has(state.activeTool)) {
      // Text callouts open their inline editor immediately — every mark asks
      // for words right away (W2 auto-open rule; pins get the details panel).
      createPointAnnotationOnStage(state.activeTool, point, {
        startTextEditing: state.activeTool === 'text',
      });
      clearTool();
      return;
    }
    if (!state.activeTool) dispatch({ type: 'annotation/clear-selection' });
  }

  function onAnnotationMouseDown(annotation, event) {
    if (event.button !== 0 || !stageRef.current) return;
    if (panModeEnabled || spaceHeldRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    // Text-on-Text is an edit gesture, even while the persistent toolbar's
    // Text tool is armed. Disarm before the subsequent click can bubble to the
    // stage and allocate a duplicate callout.
    if (!readOnly && state.activeTool === 'text' && annotation.type === 'text') {
      event.preventDefault();
      event.stopPropagation();
      // The original pointer sequence still emits a stage click after this
      // mousedown. Consume it so it cannot immediately clear edit selection.
      suppressClickRef.current = true;
      dispatch({ type: 'annotation/select', id: annotation.id });
      clearTool();
      startTextEditing(annotation.id);
      return;
    }
    // An armed creation tool wins over existing marks: let the event bubble to
    // the stage handlers so the new mark starts (TC-PMG11-TOOLS-001 — drawing
    // a highlight inside a Box used to move the Box instead). Text-on-Text,
    // resize handles, and the inline text editor keep their own handlers.
    if (
      !readOnly &&
      (DRAW_TOOLS.has(state.activeTool) ||
        PLACE_TOOLS.has(state.activeTool) ||
        state.activeTool === 'pen')
    ) {
      return;
    }
    dispatch({ type: 'annotation/select', id: annotation.id });
    if (readOnly) return;
    beginTransform(event, 'move', annotation);
  }

  const menuAnnotation =
    annotations.find((annotation) => annotation.id === menu?.annotationId) || selectedAnnotation;
  const stageStatus = stage.saveStatus || 'idle';
  // A failed package-level mutation (notably persisted-screen deletion) must
  // remain visible in the live editor even when the stage itself is still
  // saved. Otherwise the chip would falsely say "All changes saved" while the
  // retained screen needs a retry.
  const mutationFailureMessage =
    !readOnly && state.saveStatus === 'failed'
      ? state.saveMessage === 'Screen could not be deleted. Nothing was removed; try again.'
        ? state.saveMessage
        : 'Not saved to server'
      : '';
  const stageStatusTone =
    mutationFailureMessage || stageStatus === 'save_failed'
      ? 'failed'
      : stageStatus === 'autosaving'
        ? 'saving'
        : stageStatus === 'saved'
          ? 'saved'
          : 'editing';
  // Prototype save-chip copy. The /Saved/i and /Not saved to server/i spec
  // contracts still match ("All changes saved" contains "saved").
  const stageStatusMessage =
    mutationFailureMessage ||
    (stageStatus === 'save_failed'
      ? 'Not saved to server'
      : stageStatus === 'autosaving'
        ? 'Saving…'
        : stageStatus === 'saved'
          ? 'All changes saved'
          : stageStatus === 'editing'
            ? 'Editing locally'
            : 'Live draft');
  const recoveryNote =
    state.recoveryMessage ||
    (state.saveMessage?.startsWith('Recovered ') ? state.saveMessage : '');

  // ---- prototype workspace chrome (navy head · tray · stage · panel) ----
  const packageScreens = state.package?.screens || [];
  const stageScreenId = stage.screenId || stage.metadata?.screenId;
  const trayItems = packageScreens.some(
    (screen) => (screen.screenId || screen.metadata?.screenId) === stageScreenId,
  )
    ? packageScreens
    : [...packageScreens, stage];
  const activeIndex = Math.max(
    0,
    trayItems.findIndex(
      (screen) => (screen.screenId || screen.metadata?.screenId) === stageScreenId,
    ),
  );
  const trayItemFor = (screen) => {
    const screenId = screen.screenId || screen.metadata?.screenId;
    const isActive = screenId === stageScreenId;
    const marks = isActive
      ? (stage.metadata?.annotations || []).length
      : (screen.metadata?.annotations || []).length;
    return {
      screenId,
      isActive,
      marks,
      title: screen.metadata?.pageTitle || screen.metadata?.route || screenId,
      shot: screenImageSource(isActive ? stage : screen, { packageId: state.package?.id }),
      imageLostOnDevice: Boolean((isActive ? stage : screen).imageLostOnDevice),
    };
  };
  const totalMarks = trayItems.reduce((sum, screen) => sum + trayItemFor(screen).marks, 0);
  const stageSource = stage.metadata?.route
    ? `${stage.metadata.route}${stage.metadata.query || ''}${stage.metadata.hash || ''}`
    : '/home';
  const historicalMetadata = [
    { label: 'Package title', value: state.package?.title || '' },
    {
      label: 'Overall instructions',
      value: state.package?.overallInstructions || '',
    },
    { label: 'Screen', value: stage.screenId || stage.metadata?.screenId || '' },
    { label: 'Page', value: stage.metadata?.pageTitle || '' },
    {
      label: 'Route',
      value: historicalRoute(stage.metadata),
      href: safeHistoricalRoute(stage.metadata),
    },
    { label: 'Captured', value: formatSubmittedWhen(stage.metadata?.capturedAt) },
    { label: 'Submitted', value: formatSubmittedWhen(state.package?.submittedAt) },
    { label: 'Reporter', value: historicalReporter(state.package) },
    { label: 'Environment', value: stage.metadata?.environmentHost || '' },
    { label: 'Build', value: stage.metadata?.build ? `Build ${stage.metadata.build}` : '' },
    { label: 'Viewport', value: historicalViewport(stage.metadata?.viewport) },
  ].filter((entry) => entry.value);
  const zeroLinkedHistoricalWarning =
    readOnly &&
    isSystemAdmin &&
    state.package?.status === 'submitted' &&
    Number(state.package?.governedRecordSummary?.total || 0) === 0;
  const countMismatchHistoricalWarning =
    readOnly &&
    isSystemAdmin &&
    state.package?.status === 'submitted' &&
    Number(state.package?.governedRecordSummary?.total || 0) > 0 &&
    state.package?.governedRecordSummary?.integrityOk === false;
  const showHistoricalWarning = zeroLinkedHistoricalWarning || countMismatchHistoricalWarning;
  const stagePendingRegistration = packageScreens.length === 0;
  const switchToScreen = async (screenId) => {
    if (!screenId || screenId === stageScreenId) return;
    if (readOnly) {
      openScreenForEditing(screenId, { mode: 'view' });
      return;
    }
    await completeStage();
    openScreenForEditing(screenId);
  };
  const clearScreen = () => setDialog('clear');
  const discardStage = () => setDialog('discard');
  const confirmClearScreen = () => {
    replaceAnnotations([], null);
    setDialog(null);
    void flushStageAutosave();
  };
  const keepScreenAndLeave = async () => {
    if ((stage.metadata?.annotations || []).length === 0) {
      setDialog('keep-clean');
      return;
    }
    setDialog(null);
    await completeStage();
  };
  const confirmKeepCleanStage = async () => {
    setDialog(null);
    await completeStage();
  };
  const confirmDeleteStage = async () => {
    setDialog(null);
    await deleteStage();
  };

  const workspaceSubtitle = [
    `Screen ${activeIndex + 1} of ${trayItems.length}`,
    stage.metadata?.pageTitle || stage.metadata?.route || 'Live draft',
    stage.metadata?.captureMode === 'selected-region' ? 'selected region' : '',
    stage.metadata?.captureMode === 'selected-element' ? 'selected element' : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const bannerCount = (readOnly ? 1 : 0) + (recoveryNote ? 1 : 0);
  const workspaceClassName = [
    'fbws',
    bannerCount > 0 ? 'fbws-has-banner' : '',
    bannerCount > 1 ? 'fbws-has-two-banners' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={workspaceClassName} data-testid="feedback-markup-editor">
      <header className="fbws-head" data-testid="feedback-stage-action-strip">
        <div className="fbws-head-copy">
          <div className="fbws-title">Feedback workspace</div>
          <div className="fbws-sub" title={workspaceSubtitle}>
            {workspaceSubtitle}
          </div>
        </div>
        <span
          className={`fbws-savechip ${stageStatusTone}`}
          data-testid="feedback-stage-save-status"
          role="status"
          aria-live="polite"
        >
          <span className="fbws-savedot" aria-hidden />
          {readOnly ? pkgLifecycleLabel(state.package) : stageStatusMessage}
        </span>
        {readOnly ? null : (
          <div className="fbws-head-actions">
            <button
              type="button"
              className="fbws-quiet"
              data-testid="feedback-done-screen"
              onClick={keepScreenAndLeave}
              title="Finish marking this screen"
            >
              Done
            </button>
            {/* TFB-02 (Benji): the head's second Capture was ambiguous next to
                the session bar's — both capture intents now live in the bar. */}
            <button
              type="button"
              className="btn primary fbws-review"
              data-testid="feedback-review-send"
              onClick={() => void openReview()}
              disabled={packageScreens.length === 0}
              title="Review your screens — sending happens after review"
            >
              Review{totalMarks ? ` (${totalMarks})` : ''}
            </button>
            <button
              type="button"
              className="fbws-quiet"
              data-testid="feedback-save-draft"
              onClick={saveDraft}
            >
              Save
            </button>
            {state.reviewReturnContext ? (
              <button
                type="button"
                className="fbws-quiet"
                data-testid="feedback-back-to-review"
                onClick={() => void openReview({ preserveReturnContext: true })}
              >
                Back to review
              </button>
            ) : null}
            <details className="fbws-more">
              <summary data-testid="feedback-workspace-more" aria-label="More workspace actions">
                ⋯
              </summary>
              <div className="fbws-more-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  data-testid="feedback-open-history"
                  onClick={() => navigate('/feedback')}
                >
                  My Feedback
                </button>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="feedback-export-draft-zip"
                  onClick={(event) =>
                    exportPackage({ final: false, returnFocus: event.currentTarget })
                  }
                  disabled={packageScreens.length === 0}
                >
                  Export draft ZIP
                </button>
                <button
                  type="button"
                  role="menuitem"
                  data-testid="feedback-clear-screen"
                  onClick={clearScreen}
                >
                  Clear marks on this screen
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="danger"
                  data-testid="feedback-discard-stage"
                  onClick={discardStage}
                >
                  Discard this screen
                </button>
              </div>
            </details>
          </div>
        )}
      </header>

      {readOnly ? (
        <div className="fbws-recovery" data-testid="feedback-view-only-banner" role="status">
          View only — this feedback was submitted on{' '}
          {formatSubmittedWhen(state.package?.submittedAt)}. Its historical content cannot be
          edited.
        </div>
      ) : null}

      {recoveryNote ? (
        <div className="fbws-recovery" data-testid="feedback-stage-recovery-note" role="status">
          {recoveryNote}
        </div>
      ) : null}

      <div
        className={`fbws-body ${containerCompact ? 'is-container-compact' : ''}`}
        data-testid="feedback-workspace-body"
        ref={bodyRef}
        style={
          columnsAreResizable
            ? {
                '--fbws-tray-w': `${workspaceWidths.tray}px`,
                '--fbws-panel-w': `${workspaceWidths.panel}px`,
              }
            : undefined
        }
      >
        <aside className="fbws-tray" aria-label="Screens in this session">
          <div className="fbws-tray-lbl">Screens in this session</div>
          {stagePendingRegistration ? (
            <p className="fbws-tray-hint" data-testid="feedback-workspace-action-hint">
              Screens appear in the package as soon as you start marking them up.
            </p>
          ) : null}
          {trayItems.map((screen) => {
            const item = trayItemFor(screen);
            return (
              <button
                key={item.screenId}
                type="button"
                className={`fbws-thumb ${item.isActive ? 'on' : ''}`}
                data-testid={`feedback-tray-screen-${item.screenId}`}
                onClick={() => void switchToScreen(item.screenId)}
                aria-current={item.isActive ? 'true' : undefined}
              >
                <span className="fbws-thumb-shot">
                  {item.imageLostOnDevice ? (
                    <span
                      className="fbws-thumb-image-lost"
                      data-testid={`feedback-tray-image-lost-${item.screenId}`}
                    >
                      Image lost on this device
                    </span>
                  ) : item.shot ? (
                    <img src={item.shot} alt="" />
                  ) : (
                    <span className="fbws-thumb-ph" aria-hidden>
                      <i />
                      <i />
                      <i />
                    </span>
                  )}
                </span>
                <span className="fbws-thumb-name">{item.title}</span>
                <span className="fbws-thumb-meta">
                  {item.marks} mark{item.marks === 1 ? '' : 's'}
                </span>
                {item.marks ? <span className="fbws-thumb-cnt">{item.marks}</span> : null}
              </button>
            );
          })}
          {readOnly ? null : (
            <button
              type="button"
              className="fbws-thumb-add"
              aria-label="Capture another screen"
              onClick={async () => {
                await completeStage();
                navigate(stageSource);
              }}
              title="Capture another screen"
            >
              ＋
            </button>
          )}
        </aside>

        {columnsAreResizable ? (
          <WorkspaceSplitter
            label="Resize screens rail"
            testId="feedback-splitter-tray"
            value={workspaceWidths.tray}
            min={TRAY_MIN_PX}
            max={TRAY_MAX_PX}
            onResize={(next) => applyWorkspaceWidths({ ...workspaceWidths, tray: next })}
            onReset={resetWorkspaceWidths}
          />
        ) : null}

        <div className="fbws-stage-area">
          {/* Persistent visible toolbar — creation is never right-click-only.
              The context menu stays as a shortcut for power users. */}
          {readOnly ? null : (
            <MarkupToolbar
              activeTool={state.activeTool || 'select'}
              activeColor={state.activeColor}
              onToolChange={(toolId) => {
                if (toolId === 'select' || toolId === state.activeTool) clearTool();
                else armTool(toolId);
              }}
              onColorChange={(colorId) => {
                dispatch({ type: 'color/set', color: colorId });
                // With a mark selected, the toolbar swatch recolors that mark
                // too (TC-PMG11-TOOLS-002 — it used to change nothing visible).
                if (selectedAnnotation) updateAnnotationColor(selectedAnnotation.id, colorId);
              }}
              onUndo={() => dispatch({ type: 'history/undo' })}
              onRedo={() => dispatch({ type: 'history/redo' })}
              onDelete={deleteSelectedAnnotation}
              canDelete={Boolean(selectedAnnotation)}
            />
          )}

          <div
            ref={stageViewportRef}
            className={[
              'fbws-stage-viewport',
              `zoom-${stageZoom.mode}`,
              panModeEnabled || spacePanActive ? 'is-pan-ready' : '',
              isPanning ? 'is-panning' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid="feedback-stage-viewport"
            tabIndex={0}
            aria-label="Captured screen — drag with Pan or hold Space to move around when zoomed"
          >
          <div
            ref={stageRef}
            className={`feedback-markup-stage ${state.activeTool ? 'armed' : ''}`}
            data-testid="feedback-markup-stage"
            style={{
              aspectRatio: `${image.width} / ${image.height}`,
              '--fbws-ar': image.width / Math.max(1, image.height),
              ...(stageZoom.mode === 'fill'
                ? { width: '100%', maxWidth: 'none' }
                : stageZoom.mode === 'manual'
                  ? { width: `${stageZoom.level * 100}%`, maxWidth: 'none' }
                  : null),
            }}
            onMouseDown={onStageMouseDown}
            onMouseMove={handleInteractionMove}
            onMouseUp={handleInteractionEnd}
            onClick={onStageClick}
            onContextMenu={openCanvasMenu}
          >
            {stage.imageLostOnDevice ? (
              <div className="feedback-stage-image-lost" role="img" aria-label="Image lost on this device">
                Image lost on this device. Your marks and notes are still available.
              </div>
            ) : (
              <img
                src={screenImageSource(stage, { packageId: state.package?.id })}
                alt="Captured feedback evidence"
              />
            )}
            <svg viewBox={`0 0 ${image.width} ${image.height}`} aria-label="Feedback annotations">
              {renderAnnotations.map((annotation) => (
                <AnnotationShape
                  key={annotation.id}
                  annotation={annotation}
                  selected={annotation.id === state.selectedAnnotationId}
                  onMouseDown={(event) => onAnnotationMouseDown(annotation, event)}
                  onDoubleClick={(event) => {
                    if (panModeEnabled || spaceHeldRef.current) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                    event.stopPropagation();
                    if (!readOnly && annotationSupportsTextEdit(annotation.type)) {
                      dispatch({ type: 'annotation/select', id: annotation.id });
                      startTextEditing(annotation.id);
                    }
                  }}
                  onContextMenu={(event) => {
                    if (panModeEnabled || spaceHeldRef.current) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }
                    dispatch({ type: 'annotation/select', id: annotation.id });
                    if (!readOnly) {
                      event.preventDefault();
                      event.stopPropagation();
                      openObjectMenu(annotation, event);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (panModeEnabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      dispatch({ type: 'annotation/select', id: annotation.id });
                    }
                  }}
                />
              ))}
            </svg>

            {selectedAnnotation && !state.textEditingAnnotationId && !readOnly ? (
              <SelectionOverlay
                annotation={selectedAnnotation}
                image={image}
                onHandleMouseDown={(event, handle) =>
                  beginTransform(event, 'resize', selectedAnnotation, { handle })
                }
                onHandleKeyDown={(event, handle) =>
                  onTransformKeyDown(event, 'resize', selectedAnnotation, { handle })
                }
                onRotateMouseDown={(event) => beginTransform(event, 'rotate', selectedAnnotation)}
                onRotateKeyDown={(event) =>
                  onTransformKeyDown(event, 'rotate', selectedAnnotation)
                }
              />
            ) : null}

            {state.textEditingAnnotationId && selectedAnnotation?.type === 'text' && !readOnly ? (
              <div
                className="feedback-text-editor"
                style={{
                  left: stagePercent(selectedAnnotation.bounds.x, image.width),
                  top: stagePercent(selectedAnnotation.bounds.y, image.height),
                  width: stagePercent(selectedAnnotation.bounds.width, image.width),
                  height: stagePercent(selectedAnnotation.bounds.height, image.height),
                  transform: `rotate(${selectedAnnotation.rotationDeg || 0}deg)`,
                }}
              >
                <textarea
                  data-testid="feedback-text-edit-mode"
                  value={selectedAnnotation.text || ''}
                  style={
                    selectedTextLayout
                      ? {
                          fontSize: `${selectedTextLayout.metrics.fontSize}px`,
                          lineHeight: `${selectedTextLayout.metrics.lineHeight}px`,
                          padding: `${Math.max(
                            8,
                            Math.round(selectedTextLayout.metrics.insetX * 0.75),
                          )}px ${selectedTextLayout.metrics.insetX}px`,
                          backgroundColor: textBackgroundFill(
                            selectedAnnotation.style?.textBackgroundMode,
                          ),
                        }
                      : undefined
                  }
                  onChange={(event) => updateSelectedText(event.target.value)}
                  onBlur={stopTextEditing}
                  autoFocus
                />
              </div>
            ) : null}
          </div>
          </div>

          <div
            className="fbws-zoom-controls"
            role="group"
            aria-label="Stage zoom and pan"
            data-testid="feedback-zoom-controls"
          >
            <button
              type="button"
              className={`fbws-zoom-btn ${stageZoom.mode === 'fit' ? 'on' : ''}`}
              data-testid="feedback-zoom-fit"
              aria-pressed={stageZoom.mode === 'fit'}
              title="Fit the whole screenshot in view"
              onClick={resetStageView}
            >
              Fit
            </button>
            <button
              type="button"
              className={`fbws-zoom-btn ${stageZoom.mode === 'fill' ? 'on' : ''}`}
              data-testid="feedback-zoom-fill"
              aria-pressed={stageZoom.mode === 'fill'}
              title="Maximize — fill the viewing space"
              onClick={() => setStageZoom({ mode: 'fill', level: 1 })}
            >
              Max
            </button>
            <button
              type="button"
              className={`fbws-zoom-btn fbws-pan-btn ${panModeEnabled ? 'on' : ''}`}
              data-testid="feedback-pan-toggle"
              aria-label="Pan"
              aria-pressed={panModeEnabled}
              title="Pan — drag the screenshot to move around"
              onClick={() => setPanModeEnabled((current) => !current)}
            >
              <span aria-hidden>✋</span>
              <span>Pan</span>
            </button>
            <button
              type="button"
              className="fbws-zoom-btn"
              data-testid="feedback-zoom-out"
              aria-label="Zoom out"
              title="Zoom out"
              onClick={() => applyManualZoom((level) => steppedZoom(level, -1))}
            >
              −
            </button>
            <span className="fbws-zoom-readout" data-testid="feedback-zoom-readout" aria-live="polite">
              {stageZoom.mode === 'fit' ? 'Fit' : stageZoom.mode === 'fill' ? 'Max' : `${Math.round(stageZoom.level * 100)}%`}
            </span>
            <button
              type="button"
              className="fbws-zoom-btn"
              data-testid="feedback-zoom-in"
              aria-label="Zoom in"
              title="Zoom in — mouse wheel over the screenshot also zooms at the cursor"
              onClick={() => applyManualZoom((level) => steppedZoom(level, 1))}
            >
              +
            </button>
          </div>

          {(stage.metadata?.annotations || []).length === 0 && !draftAnnotation ? (
            <div className="fbws-stage-hint" aria-hidden>
              <span className="fbws-hint-dot" />
              Mark anything you&apos;d like changed — a click drops a pin
            </div>
          ) : null}
        </div>

        {columnsAreResizable ? (
          <WorkspaceSplitter
            label="Resize comments panel"
            testId="feedback-splitter-panel"
            value={workspaceWidths.panel}
            min={PANEL_MIN_PX}
            max={PANEL_MAX_PX}
            invert
            onResize={(next) => applyWorkspaceWidths({ ...workspaceWidths, panel: next })}
            onReset={resetWorkspaceWidths}
          />
        ) : null}

        <aside className="fbws-panel">
          {readOnly && (historicalMetadata.length > 0 || showHistoricalWarning) ? (
            <section
              className="feedback-historical-metadata"
              data-testid="feedback-historical-metadata"
            >
              <div className="feedback-panel-kicker">Historical context</div>
              {showHistoricalWarning ? (
                <div
                  className="feedback-historical-warning"
                  data-testid="feedback-historical-warning"
                  role="status"
                >
                  {zeroLinkedHistoricalWarning
                    ? 'Submission integrity warning — no linked governed feedback records were found for this package.'
                    : "Submission integrity warning — linked governed feedback records do not match this package's submitted record count."}
                </div>
              ) : null}
              <dl className="feedback-historical-grid">
                {historicalMetadata.map((entry) => (
                  <div key={entry.label} className="feedback-historical-row">
                    <dt className="feedback-historical-label">{entry.label}</dt>
                    <dd className="feedback-historical-value">
                      {entry.value}
                      {entry.href ? (
                        <a
                          className="feedback-historical-origin-link"
                          href={entry.href}
                          aria-label={`Open originating screen: ${entry.value}`}
                        >
                          Open originating screen →
                        </a>
                      ) : null}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}
          <AnnotationListPanel
            annotations={stage.metadata?.annotations || []}
            actorUserId={user?.id || null}
            selectedAnnotationId={state.selectedAnnotationId}
            readOnly={readOnly}
            onSelect={(annotationId) => dispatch({ type: 'annotation/select', id: annotationId })}
            onUpdate={updateAnnotation}
            onDelete={(annotationId) => dispatch({ type: 'annotation/delete', id: annotationId })}
            onColorChange={updateAnnotationColor}
            suppressSelectionFocus={Boolean(state.textEditingAnnotationId)}
          />
          {selectedAnnotation && !readOnly ? (
            <div className="fbws-panel-done">
              <button
                type="button"
                className="btn primary"
                onClick={() => dispatch({ type: 'annotation/clear-selection' })}
              >
                ✓ Done — back to the screenshot
              </button>
            </div>
          ) : null}
        </aside>
      </div>

      {readOnly ? null : (
        <div ref={menuRef}>
          <ContextMenu
            menu={menu}
            annotation={menuAnnotation}
            onCreate={(tool) => {
              armTool(tool);
              setMenu(null);
            }}
            onAction={(action) => handleObjectAction(action, menuAnnotation)}
            onColorChange={(kind, value) => updateSelectedColor(kind, value, menuAnnotation)}
            onTextBackgroundChange={(mode) =>
              updateSelectedTextBackground(mode, menuAnnotation)
            }
            onNoteChange={(value) => {
              if (menuAnnotation?.type === 'text') updateSelectedText(value, menuAnnotation);
              else updateSelectedNote(value, menuAnnotation);
            }}
          />
        </div>
      )}

      {dialog === 'clear' ? (
        <div className="feedback-modal-backdrop" role="presentation">
          <section
            className="feedback-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Clear screen confirmation"
            data-testid="feedback-clear-screen-dialog"
          >
            <div className="feedback-panel-kicker">Clear screen</div>
            <h3>Remove all annotations from this screen?</h3>
            <p>The screenshot stays in the package. Only the markup is removed.</p>
            <div className="feedback-confirm-actions">
              <button type="button" className="btn ghost" onClick={() => setDialog(null)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={confirmClearScreen}>
                Clear annotations
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {dialog === 'discard' ? (
        <div className="feedback-modal-backdrop" role="presentation">
          <section
            className="feedback-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Discard stage options"
            data-testid="feedback-discard-stage-dialog"
          >
            <div className="feedback-panel-kicker">Discard stage</div>
            <h3>Choose what to do with this live draft.</h3>
            <p>You can keep the screen and leave the editor, or delete it completely.</p>
            <div className="feedback-confirm-actions stacked">
              <button type="button" className="btn primary" onClick={keepScreenAndLeave}>
                Keep screen and leave editor
              </button>
              <button type="button" className="btn danger" onClick={confirmDeleteStage}>
                Delete screen
              </button>
              <button type="button" className="btn ghost" onClick={() => setDialog(null)}>
                Cancel
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {dialog === 'keep-clean' ? (
        <div className="feedback-modal-backdrop" role="presentation">
          <section
            className="feedback-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Keep clean screenshot options"
            data-testid="feedback-keep-clean-dialog"
          >
            <div className="feedback-panel-kicker">Keep clean screenshot</div>
            <h3>This screen has no markup yet</h3>
            <p>You can still send the clean screenshot as evidence, or discard this screen now.</p>
            <div className="feedback-confirm-actions stacked">
              <button type="button" className="btn primary" onClick={confirmKeepCleanStage}>
                Keep the screenshot
              </button>
              <button type="button" className="btn danger" onClick={confirmDeleteStage}>
                Discard this screen
              </button>
              <button type="button" className="btn ghost" onClick={() => setDialog(null)}>
                Cancel
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
